export namespace UrlUtils {
    /**
     * Retrieves the value of a query parameter from the specified URL.
     *
     * @param name - The name of the query parameter to retrieve.
     * @param {string} [url=null] - The URL to retrieve the query parameter from. If not provided, the current URL is used.
     * @param {boolean} [caseSensitive=true] - Indicates whether the search should be case-sensitive.
     * @param {boolean} [replacePlusWithSpace=true] - Indicates whether '+' characters should be replaced with spaces.
     * @return {string | null} - The value of the query parameter, or null if it is not found.
     */
    export const getQueryString = (
        name: string,
        url: string = null,
        caseSensitive = true,
        replacePlusWithSpace = true
    ): string => {
        if (!url) url = window.location.href;
        name = name.replace(/[[\]]/g, "\\$&");
        const expression = "[?&]" + name + "(=([^&#]*)|&|#|$)";
        const regex = caseSensitive ? new RegExp(expression) : new RegExp(expression, "i");
        const results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return "";
        const replacedResult = replacePlusWithSpace ? results[2].replace(/\+/g, " ") : results[2];
        return decodeURIComponent(replacedResult);
    };

    /**
     * Clears the specified URL parameters from the current URL and updates the browser history.
     *
     * @param {string[]} urlParams - An optional array of URL parameters to clear. If not provided, all URL parameters will be cleared.
     * @return {void}
     */
    export const clearUrlParams = (urlParams?: string[]): void => {
        let newUrl = window.location.href;

        // If no parameters are provided, clear all parameters
        if (urlParams === undefined) {
            const url = new URL(window.location.href);
            url.search = "";
            url.hash = "";
            newUrl = url.toString();
        } else if (urlParams.length > 0) {
            // Clear the specified parameters
            for (const element of urlParams) {
                const parameter = element;
                newUrl = newUrl
                    .replace(new RegExp("[?&]" + parameter + "=[^&#]*(#.*)?$"), "$1")
                    .replace(new RegExp("([?&])" + parameter + "=[^&]*&"), "$1");
            }
        } else {
            // No parameters to clear
            return;
        }

        if (newUrl === window.location.href) {
            return;
        }

        if (window.history) {
            window.history.replaceState(null, document.title, newUrl);
        }
    };

    /**
     * Calls clearUrlParams function with the provided urlParam.
     *
     * @param {string} urlParam - The URL parameter to clear.
     * @return {void}
     */
    export const clearUrlParam = (urlParam: string) => {
        clearUrlParams([urlParam]);
    };

    /**
     * Retrieves the URL without any query parameters.
     *
     * @returns {string} - The URL without any query parameters.
     */
    export const getUrlWithoutParams = () => {
        return window.location.href.split("?")[0];
    };

    export const removeUrlPath = (urlPath: string): void => {
        if (!window.location.href.includes(urlPath)) {
            return;
        }

        let newUrl = window.location.href;
        newUrl = newUrl.replace(urlPath, "");

        if (window.history) {
            window.history.replaceState(null, document.title, newUrl);
        }
    };
}
